import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer } from 'react';
import { LOGIN, LOGOUT } from 'store/actions';
import accountReducer from 'store/accountReducer';
import Loader from 'ui-component/Loader';

const API_URL = `${process.env.REACT_APP_API_BASE}/api/auth`;

const initialState = {
    isLoggedIn: false,
    isInitialized: false,
    user: null
};

// ==============================|| API AUTH CONTEXT ||============================== //

const APIContext = createContext(null);

export const APIProvider = ({ children }) => {
    const [state, dispatch] = useReducer(accountReducer, initialState);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            dispatch({ type: LOGOUT });
            return;
        }
        fetch(`${API_URL}/me/`, {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then((res) => (res.ok ? res.json() : Promise.reject()))
            .then(({ user }) => {
                localStorage.removeItem('loginPortal');
                dispatch({ type: LOGIN, payload: { isLoggedIn: true, user } });
            })
            .catch(() => {
                localStorage.removeItem('token');
                dispatch({ type: LOGOUT });
            });
    }, []);

    // Called by AuthRegister.js
    const firebaseRegister = async (email, password, extraData = {}) => {
        const res = await fetch(`${API_URL}/register/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password, ...extraData })
        });
        const data = await res.json();
        if (!res.ok) {
            const msg = data.errors ? Object.values(data.errors)[0] : data.error || 'Registration failed.';
            throw new Error(msg);
        }
        localStorage.setItem('token', data.token);
        dispatch({ type: LOGIN, payload: { isLoggedIn: true, user: data.user } });
        return data;
    };

    // Called by AuthLogin.js
    const firebaseEmailPasswordSignIn = async (email, password) => {
        const res = await fetch(`${API_URL}/login/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password })
        });
        const data = await res.json();
        if (!res.ok) {
            throw new Error(data.error || 'Login failed.');
        }
        localStorage.setItem('token', data.token);
        dispatch({ type: LOGIN, payload: { isLoggedIn: true, user: data.user } });
        return data;
    };

    const forceChangePassword = async (newPassword, confirmPassword) => {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/force-change-password/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ newPassword, confirmPassword })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to change password.');
        // Refresh user state so mustChangePassword becomes false
        const meRes = await fetch(`${API_URL}/me/`, { headers: { Authorization: `Bearer ${token}` } });
        if (meRes.ok) {
            const { user } = await meRes.json();
            dispatch({ type: LOGIN, payload: { isLoggedIn: true, user } });
        }
        return data;
    };

    const logout = () => {
        localStorage.removeItem('token');
        dispatch({ type: LOGOUT });
    };

    const setInitialPin = async (newPin, confirmPin) => {
        const tkn = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/set-pin/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tkn}` },
            body: JSON.stringify({ newPin, confirmPin })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to set PIN.');
        const meRes = await fetch(`${API_URL}/me/`, { headers: { Authorization: `Bearer ${tkn}` } });
        if (meRes.ok) {
            const { user } = await meRes.json();
            dispatch({ type: LOGIN, payload: { isLoggedIn: true, user } });
        }
        return data;
    };

    const resetPassword = async () => {};
    const updateProfile = () => {};

    if (!state.isInitialized) {
        return <Loader />;
    }

    return (
        <APIContext.Provider
            value={{
                ...state,
                firebaseRegister,
                firebaseEmailPasswordSignIn,
                forceChangePassword,
                logout,
                resetPassword,
                updateProfile,
                setInitialPin
            }}
        >
            {children}
        </APIContext.Provider>
    );
};

APIProvider.propTypes = {
    children: PropTypes.node
};

export default APIContext;
